import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import {
    DialogContent,
    makeStyles,
} from '@material-ui/core';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import PhoneUtils from 'lib/PhoneUtils';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        padding: theme.spacing(3),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    headerText: {
        fontWeight: 'bold',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
        textTransform: 'uppercase',
        fontSize: '0.8rem',
    },
    enteredCustomerCard: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: '8px',
        padding: theme.spacing(2),
    },
    enteredName: {
        fontWeight: 'bold',
        fontSize: '1.2rem',
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.contrastText,
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1.5),
        fontSize: '0.9rem',
        '& svg': {
            marginRight: theme.spacing(1),
            fontSize: '1.2rem',
            opacity: 0.8,
        },
    },
    duplicateList: {
        maxHeight: '360px',
        overflowY: 'auto',
        paddingRight: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: '4px',
        },
    },
    duplicateCard: {
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: '8px',
        padding: theme.spacing(2),
        transition: 'all 0.2s',
        backgroundColor: theme.palette.background.paper,
        minHeight: 110,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
    },
    duplicateCardSelectable: {
        cursor: 'pointer',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    },
    duplicateCardSelected: {
        border: `1px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.action.selected,
    },
    dupHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    dupName: {
        fontSize: '1rem',
        color: theme.palette.text.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        paddingRight: theme.spacing(1),
    },
    customerLink: {
        marginLeft: theme.spacing(1),
    },
    matchTag: {
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        flexShrink: 0,
    },
    matchHigh: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.dark,
    },
    matchMed: {
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.dark,
    },
    matchLow: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[700],
    },
    dupDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
        flex: 1,
    },
    dupContactRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    dupContact: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        minWidth: 0,
        overflow: 'hidden',
        '& svg': {
            fontSize: '1rem',
            marginRight: '4px',
            verticalAlign: 'text-bottom',
        },
    },
    dupAddressRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginTop: 'auto',
    },
    dupAddress: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        flex: 1,
        overflow: 'hidden',
        '& svg': {
            fontSize: '1rem',
            marginRight: '4px',
            flexShrink: 0,
        },
    },
    addressText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    businessLabel: {
        fontSize: '0.7rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
}));

const EMPTY = '--';

const normalizeCustomers = (suggestedCustomer) => {
    if (Array.isArray(suggestedCustomer)) return suggestedCustomer;
    if (suggestedCustomer) return [suggestedCustomer];
    return [];
};

const DuplicateSuggestStep = ({
    suggestedCustomer,
    enteredCustomer,
    selectable,
    selectedIndex,
    onSelectIndex,
}) => {
    const classes = useStyles();
    const customers = normalizeCustomers(suggestedCustomer);

    useEffect(() => {
        if (!selectable && selectedIndex != null) {
            onSelectIndex(null);
        }
    }, [selectable, selectedIndex, onSelectIndex]);

    const renderMatchTag = (matchValue) => {
        const pct = matchValue || 0;
        let tagClass = classes.matchLow;
        if (pct >= 90) tagClass = classes.matchHigh;
        else if (pct >= 75) tagClass = classes.matchMed;

        return (
            <div className={`${classes.matchTag} ${tagClass}`}>
                {pct}
                % MATCH
            </div>
        );
    };

    const renderDate = (customer) => {
        if (customer.modifiedOn) {
            return `Updated ${moment(customer.modifiedOn).fromNow()}`;
        }
        if (customer.sysDate) {
            return `Created ${moment(customer.sysDate).fromNow()}`;
        }
        return '';
    };

    const getFullName = (customerObj) => {
        if (!customerObj) return EMPTY;
        return [customerObj.firstName, customerObj.lastName].filter(Boolean).join(' ') || EMPTY;
    };

    const enteredFullName = getFullName(enteredCustomer);

    return (
        <DialogContent className={classes.dialogContent}>
            <Row>
                <Col xs={12} sm={5}>
                    <div className={classes.headerText}>Entered Customer</div>
                    <div className={classes.enteredCustomerCard}>
                        <div className={classes.enteredName}>{enteredFullName}</div>
                        <div className={classes.infoRow}>
                            <MailOutlineIcon />
                            <span>{enteredCustomer?.email || EMPTY}</span>
                        </div>
                        <div className={classes.infoRow}>
                            <PhoneOutlinedIcon />
                            <span>{PhoneUtils.format(enteredCustomer?.cellPhone) || EMPTY}</span>
                        </div>
                        <div className={classes.infoRow}>
                            <LocationOnOutlinedIcon />
                            <span>{enteredCustomer?.address1 || EMPTY}</span>
                        </div>
                    </div>
                </Col>

                <Col xs={12} sm={7}>
                    <div className={classes.headerText}>
                        Potential Duplicates Found (
                        {customers.length}
                        )
                    </div>
                    <div className={classes.duplicateList}>
                        {customers.length === 0 && (
                            <div className="text-muted">No customer found</div>
                        )}
                        {customers.map((c, index) => {
                            const isSelected = selectable && selectedIndex === index;
                            const fullName = getFullName(c);
                            const cellPhone = PhoneUtils.format(c.cellPhone) || EMPTY;

                            return (
                                <div
                                    key={c.customerId || c.customerCode || index}
                                    className={[
                                        classes.duplicateCard,
                                        selectable ? classes.duplicateCardSelectable : '',
                                        isSelected ? classes.duplicateCardSelected : '',
                                    ].filter(Boolean).join(' ')}
                                    {...(selectable ? {
                                        role: 'button',
                                        tabIndex: 0,
                                        onClick: () => onSelectIndex(index),
                                        onKeyDown: (e) => {
                                            if (e.target !== e.currentTarget) return;
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onSelectIndex(index);
                                            }
                                        },
                                    } : {})}
                                >
                                    <div className={classes.dupHeader}>
                                        <div className={classes.dupName}>
                                            {fullName}
                                            {c.customerCode && (
                                                <a
                                                    href={`/customers/${c.customerCode}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`${classes.customerLink} link`}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    #
                                                    {c.customerCode}
                                                </a>
                                            )}
                                        </div>
                                        {renderMatchTag(c.matchPercentage)}
                                    </div>
                                    <div className={classes.dupDetails}>
                                        <div className={classes.dupContactRow}>
                                            <div className={classes.dupContact}>
                                                <span>
                                                    <MailOutlineIcon />
                                                    {' '}
                                                    {c.email || EMPTY}
                                                </span>
                                                <span>&bull;</span>
                                                <span>
                                                    <PhoneOutlinedIcon />
                                                    {' '}
                                                    {cellPhone}
                                                </span>
                                            </div>
                                            <div>
                                                {renderDate(c)}
                                            </div>
                                        </div>
                                        <div className={classes.dupAddressRow}>
                                            <div className={classes.dupAddress}>
                                                <LocationOnOutlinedIcon />
                                                <span className={classes.addressText}>
                                                    {c.address1 || EMPTY}
                                                </span>
                                            </div>
                                            {c.isBusiness && (
                                                <div className={classes.businessLabel}>Business</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Col>
            </Row>
        </DialogContent>
    );
};

DuplicateSuggestStep.propTypes = {
    suggestedCustomer: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array,
    ]),
    enteredCustomer: PropTypes.object,
    selectable: PropTypes.bool,
    selectedIndex: PropTypes.number,
    onSelectIndex: PropTypes.func.isRequired,
};

DuplicateSuggestStep.defaultProps = {
    suggestedCustomer: [],
    enteredCustomer: {},
    selectable: true,
    selectedIndex: null,
};

export default DuplicateSuggestStep;
